/********************************************************************
 * Copyright (c) Certus Software, 2021.                             *
 *                                                                  *
 * This unpublished material is proprietary to Certus Software.     *
 * All rights reserved. The methods and techniques described        *
 * herein are considered confidential and/or trade secrets.         *
 * Reproduction or distribution, in whole or in part, is forbidden  *
 * except by express written permission of Certus Software.         *
 ********************************************************************/

import {Injectable} from '@angular/core';
import {User, UserType} from '../_models/user';
import {ApiService} from './api.service';
import {Router} from '@angular/router';
import {AuthService} from './auth.service';
import {Report} from '../_models/report';
import {SpinnerServiceService} from './spinner-service.service';
import {environment} from 'src/environments/environment';
import {BehaviorSubject, Observable, ReplaySubject} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {CookieDisabledComponent} from '../components/cookie-disabled/cookie-disabled.component';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {TranslateService} from '@ngx-translate/core';
import {AccountStats} from '../_models/account-stats';

@Injectable()
export class UserService {
  private currentUserSubject = new BehaviorSubject<User>({} as User);
  public currentUser = this.currentUserSubject.asObservable().pipe(distinctUntilChanged());

  private isAuthenticatedSubject = new ReplaySubject<boolean>(1);
  public isAuthenticated = this.isAuthenticatedSubject.asObservable();

  // Guest user stats
  private guestStatsSubject = new ReplaySubject<Report>();
  public currentGuestStats = this.guestStatsSubject.asObservable().pipe(distinctUntilChanged());

  // Registered user license totals
  private licenseStatsSubject = new ReplaySubject<AccountStats>();
  public currentLicenseStats = this.licenseStatsSubject.asObservable().pipe(distinctUntilChanged());

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router,
    private spinner: SpinnerServiceService,
    public dialog: MatDialog,
    public translate: TranslateService
  ) {}

  attemptAccountRegistrationStep1(credentials): Observable<void> {
    return this.apiService.post('/rest-api/web/registered/v1.0/user/register/send-email', credentials);
  }

  attemptAccountRegistrationStep2(credentials): Observable<void> {
    return this.apiService.post('/rest-api/web/registered/v1.0/user', credentials);
  }

  attemptAccountRegistrationTokenValidation(postData): Observable<void> {
    return this.apiService.post('/rest-api/web/registered/v1.0/user/register/validate', postData);
  }

  attemptAccountRecovery(credentials): Observable<void> {
    return this.apiService.post('/rest-api/web/registered/v1.0/user/reset-password/send-email', credentials);
  }

  attemptChangePassword(credentials): Observable<void> {
    return this.apiService.post('/rest-api/web/registered/v1.0/user/change-password', credentials);
  }

  attemptAccountResetPasswordTokenValidation(postData): Observable<void> {
    return this.apiService.post('/rest-api/web/registered/v1.0/user/reset-password/email/validate', postData);
  }

  attemptAccountResetPassword(postData): Observable<void> {
    return this.apiService.post('/rest-api/web/registered/v1.0/user/reset-password/email', postData);
  }

  attemptAccountAuth(credentials): Observable<void> {
    return this.apiService.post('/rest-api/web/registered/v1.0/authentication', credentials)
      .pipe(map(
        data => {
          this.setAuthorizationToken(data.token);
        }
      ));
  }

  attemptGuestAuth(credentials): Observable<void> {
    return this.apiService.post('/rest-api/web/unregistered/v1.0/authentication', credentials)
      .pipe(map(
        data => {
          this.setAuthorizationToken(data.token);
          this.authService.destroyCodeToken();
        }
      ));
  }

  setAuthorizationToken(token: string) {
    this.authService.saveAuthorizationToken(token);
    this.isAuthenticatedSubject.next(true);
  }

  loadAccountUser(isLogin: boolean) {
    this.apiService.get('/rest-api/web/registered/v1.0/user', {})
      .subscribe(
        data => {
          this.licenseStatsSubject.next(data);
          this.isAuthenticatedSubject.next(true);

          this.currentLicenseStats.subscribe(
            (currentLicenseStats: any) => {
              const user = new User();
              user.emailOrLicense = currentLicenseStats.email;
              user.userType = UserType.Registered;
              this.currentUserSubject.next(user);
            });

          if (isLogin) {
            window.location.href = `${environment.redirectBasePath}`;
          } else {
            this.spinner.stop();
          }
        },
        err => {
          this.purgeAuth();
          this.router.navigate(['/login']);
        }
      );
  }

  loadGuestUser(isLogin: boolean) {
    this.apiService.get('/rest-api/web/unregistered/v1.0/code', {})
      .subscribe(
        data => {
          this.guestStatsSubject.next(data);
          this.isAuthenticatedSubject.next(true);

          this.currentGuestStats.subscribe(
            (currentGuestStats: any) => {
              const user = new User();
              user.emailOrLicense = currentGuestStats.code;
              user.userType = UserType.Unregistered;
              this.currentUserSubject.next(user);
            });

          if (isLogin) {
            window.location.href = `${environment.redirectBasePath}`;
          } else {
            this.spinner.stop();
          }
      },
      err => {
        this.purgeAuth();
        this.router.navigate(['/login']);
      }
    );
  }

  getCurrentUser(): User {
    return this.currentUserSubject.getValue();
  }

  purgeAuth() {
    // Remove JWT from cookies
    this.authService.destroyAuthorizationToken();
    // Set current user to an empty object
    this.currentUserSubject.next({} as User);
    this.guestStatsSubject.next({} as Report);
    this.licenseStatsSubject.next({} as AccountStats);
    // Set auth status to false
    this.isAuthenticatedSubject.next(false);
    // Close all dialogs and snackbars
    /*this.dialogRefs.closeAll();
    this.snackBars.dismiss();*/
  }

  // Verify JWT in cookies with server & load user's info.
  // This runs once on application startup.
  populate() {
    // If JWT detected, attempt to get & store user's info
    if (this.authService.getAuthorizationToken()) {
      let userRole = JSON.parse(atob((this.authService.getAuthorizationToken()).split('.')[1])).scope;
      if(userRole === UserType.Registered) {
        this.loadAccountUser(false);
      } else if(userRole === UserType.Unregistered) {
        this.loadGuestUser(false);
      } else {
        this.purgeAuth();
      }
    } else {
      if (!this.authService.areCookiesEnabled()) {
        this.dialog.open(CookieDisabledComponent, {
          width: '50%'
        });
      }
      // Remove any potential remnants of previous auth states
      this.purgeAuth();
    }
  }

  initGuestStats() {
    return new Promise(resolve => {
      this.currentGuestStats.subscribe(
        (data: any) => {
          resolve(data);
        });
    });
  }

  initAccountStats() {
    return new Promise(resolve => {
      this.currentLicenseStats.subscribe(
        (data: any) => {
          resolve(data);
        });
    });
  }

  // Todo: Vezi cum ramane cu astea
  // setCurrentSoftwareDownloadDetails(): Observable<Downloads> {
  //   return this.apiService.get('/downloads');
  // }
  // setCutDownloadDetails(): Observable<any> {
  //   return this.apiService.getAppResource('/download/products/ceceut/' + this.translate.currentLang + '/version');
  // }
}
